import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import Person from '@/models/Customers/People/Person';
import UxAssociation from '@/models/Customers/Associations/UxAssociation';
import Address from '@/models/Customers/Addresses/Address';
import UpdatePersonRequest from '@/models/Customers/People/UpdatePersonRequest';
import User from '@/models/Settings/Users/User';
import Customer from '@/models/Customers/Customers/Customer';
import PermissionUtils from '@/lib/permissionUtils';
import ValidationUtils from '@/lib/validationUtils';

export interface IPersonState {
    loading: boolean;
    loadingAddresses: boolean;
    loadingRelatedCustomers: boolean;
    loadingRelatedRecords: boolean;
    person: Person;
    people: Person[];
    peopleSearch: string;
    totalPeoplePages: number;
    addresses: Address[];
    totalAddressesPages: number;
    relatedCustomers: Customer[];
    totalRelatedCustomersPages: number;
    relatedRecords: UxAssociation[];
    totalRelatedRecordsPages: number;
    user: User;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'PersonModule', namespaced: true })
class PersonStore extends VuexModule implements IPersonState {

    public loading: boolean = true;
    public loadingAddresses: boolean = true;
    public loadingRelatedCustomers: boolean = true;
    public loadingRelatedRecords: boolean = true;
    public person: Person = new Person();
    public people: Person[] = [];
    public peopleSearch: string = '';
    public totalPeoplePages: number = 1;
    public addresses: Address[] = [];
    public totalAddressesPages: number = 1;
    public relatedCustomers: Customer[] = [];
    public totalRelatedCustomersPages: number = 1;
    public relatedRecords: UxAssociation[] = [];
    public totalRelatedRecordsPages: number = 1;
    public user: User = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getPeople(pageNumber: number) {

        this.setLoading(true);

        const searchParameter = this.peopleSearch ? `&name=${ValidationUtils.encodeRFC5987ValueChars(this.peopleSearch)}` : '';

        // Allow the ability to search for as many people as the API will give by
        // not passing in a page size & page number if the page number parameter is not set
        let pageSizeParameter = '';
        let pageNumberParameter = '';
        if (pageNumber) {
            pageSizeParameter = `&pageSize=${this.pageSize}`;
            pageNumberParameter = `&pageNumber=${pageNumber}`;
        }

        await ajax.get(`/portal-backend/ux-api/people?${pageSizeParameter}${pageNumberParameter}${searchParameter}`)
        .then(response => {
            this.context.commit("setPeople", response.data.results);
            this.context.commit("setPeopleTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async updatePeopleSearch(searchText: string) {
        // For a smoother UI experience we immidiately set the loading flag
        // and then reset the people and page count all before
        // searching, this makes the UI look faster and avoids old data hanging around
        this.setLoading(true);
        this.context.commit("resetPeople");
        this.context.commit("setPeopleTotalPages", 1);
        this.context.commit("setPeopleSearch", searchText);
    }

    @Action
    async getPerson(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/ux-api/people/${id}`)
        .then(response => {
            this.context.commit("setPerson", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getUser(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/ux-api/users/${id}`)
        .then(response => {
            this.context.commit("setUser", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async editPerson(request: UpdatePersonRequest) {

        this.setLoading(true);

        await ajax.patch(`/portal-backend/ux-api/people/${this.person.id}`, request)
        .then(response => {
            this.context.commit("setPerson", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getAddresses(pageNumber: number) {

        this.setLoadingAddresses(true);

        await ajax.get(`/portal-backend/ux-api/people/${this.person.id}/addresses?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setAddresses", response.data.results);
            this.context.commit("setAddressesTotalPages", response.data.records.totalRecords);
            this.setLoadingAddresses(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingAddresses(false);
        });
    }

    @Action
    async getRelatedCustomers(pageNumber: number) {

        this.setLoadingRelatedCustomers(false);

        await ajax.get(`/portal-backend/ux-api/people/${this.person.id}/customers?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setRelatedCustomers", response.data.results);
            this.context.commit("setRelatedCustomersTotalPages", response.data.records.totalRecords);
            this.setLoadingRelatedCustomers(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingRelatedCustomers(false);
        });
    }

    @Action
    async getRelatedRecords(pageNumber: number) {

        this.setLoadingRelatedRecords(true);

        await ajax.get(`/portal-backend/ux-api/people/${this.person.id}/associations?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setRelatedRecords", response.data.associations);
            this.context.commit("setRelatedRecordsTotalPages", response.data.records.totalRecords);
            this.setLoadingRelatedRecords(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingRelatedRecords(false);
        });
    }

    @Action
    public async cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingAddresses = false;
        this.loadingRelatedRecords = false;
        this.loadingRelatedCustomers = false;
        this.person = new Person();
        this.people = [];
        this.peopleSearch = '';
        this.totalPeoplePages = 1;
        this.addresses =[];
        this.totalAddressesPages = 1;
        this.relatedCustomers = [];
        this.totalRelatedCustomersPages = 1;
        this.relatedRecords = [];
        this.totalRelatedRecordsPages = 1;
        this.user = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingAddresses(loading: boolean) {
        this.loadingAddresses = loading;
    }

    @Mutation
    private async setLoadingRelatedCustomers(loading: boolean) {
        this.loadingRelatedCustomers = loading;
    }

    @Mutation
    private async setLoadingRelatedRecords(loading: boolean) {
        this.loadingRelatedRecords = loading;
    }

    @Mutation
    private async setPerson(person: Person) {
        this.person = person;
    }

    @Mutation
    private async setPeople(response: Person[]) {
        if (response) {
            this.people = response;
        }
    }

    @Mutation
    private async resetPeople() {
        this.people = [];
    }

    @Mutation
    private async setPeopleSearch(text: string) {
        this.peopleSearch = text;
    }

    @Mutation
    private async setPeopleTotalPages(total: number) {
        this.totalPeoplePages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setUser(user: User) {
        this.user = user;
    }

    @Mutation
    private async setAddresses(response: Address[]) {
        if (response) {
            this.addresses = response;
        }
    }

    @Mutation
    private async setAddressesTotalPages(total: number) {
        this.totalAddressesPages =  Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRelatedCustomers(response: Customer[]) {
        if (response) {
            this.relatedCustomers = response;
        }
    }

    @Mutation
    private async setRelatedCustomersTotalPages(total: number) {
        this.totalRelatedCustomersPages =  Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRelatedRecords(response: UxAssociation[]) {

        if (response) {
            this.relatedRecords = response;
        }

        // TODO: Check why we do this mapping
        for (let index = 0; index < this.relatedRecords.length; index++) {
            const association = this.relatedRecords[index];
            association.entityIdentifier.entityId = association.organisationIdentifier.entityId;
            association.entityIdentifier.entityName = association.organisationIdentifier.entityName;
            association.entityIdentifier.entityType = association.organisationIdentifier.entityType;
        }
    }

    @Mutation
    private async setRelatedRecordsTotalPages(total: number) {
        this.totalRelatedRecordsPages =  Math.ceil(total/this.pageSize);
    }

}

export const PersonModule = getModule(PersonStore);